import { useState } from 'react';
import { Template } from '../../api/client';
import { useClientTemplates } from '../../hooks/useClientData';

export function TemplatesPage() {
  const [selectedTemplate, setSelectedTemplate] = useState<Template | null>(null);
  const [selectedIndustry, setSelectedIndustry] = useState('');
  const [selectedCompany, setSelectedCompany] = useState('');
  const [selectedLabelKey, setSelectedLabelKey] = useState('');

  const { data: templates, isLoading } = useClientTemplates();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  const allTemplates = templates || [];
  const industries = Array.from(
    new Set(allTemplates.map((template) => template.industry || 'unknown'))
  ).sort();

  const companies = Array.from(
    new Set(
      allTemplates
        .filter((template) => (selectedIndustry ? template.industry === selectedIndustry : true))
        .map((template) => template.company || template.retailer)
    )
  ).sort();

  const labels = allTemplates.filter((template) => {
    if (selectedIndustry && template.industry !== selectedIndustry) return false;
    if (selectedCompany && (template.company || template.retailer) !== selectedCompany) return false;
    return true;
  });

  const labelOptions = labels.map((template) => ({
    value: template.templateKey,
    label: `${template.templateName || template.templateKey} (${template.labelType})`,
  }));

  const selectedFromList = selectedLabelKey
    ? allTemplates.find((template) => template.templateKey === selectedLabelKey) || null
    : null;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-900">Templates</h1>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="card space-y-4">
          <div>
            <label className="label">Industry</label>
            <select
              className="input"
              value={selectedIndustry}
              onChange={(e) => {
                setSelectedIndustry(e.target.value);
                setSelectedCompany('');
                setSelectedLabelKey('');
                setSelectedTemplate(null);
              }}
            >
              <option value="">Select industry...</option>
              {industries.map((industry) => (
                <option key={industry} value={industry}>
                  {industry}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="label">Company</label>
            <select
              className="input"
              value={selectedCompany}
              onChange={(e) => {
                setSelectedCompany(e.target.value);
                setSelectedLabelKey('');
                setSelectedTemplate(null);
              }}
              disabled={!selectedIndustry}
            >
              <option value="">Select company...</option>
              {companies.map((company) => (
                <option key={company} value={company}>
                  {company}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="label">Label</label>
            <select
              className="input"
              value={selectedLabelKey}
              onChange={(e) => {
                setSelectedLabelKey(e.target.value);
                const match = allTemplates.find((template) => template.templateKey === e.target.value) || null;
                setSelectedTemplate(match);
              }}
              disabled={!selectedCompany}
            >
              <option value="">Select label...</option>
              {labelOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          <div className="pt-2">
            <button
              type="button"
              className="btn btn-secondary w-full"
              onClick={() => {
                setSelectedIndustry('');
                setSelectedCompany('');
                setSelectedLabelKey('');
                setSelectedTemplate(null);
              }}
              disabled={!selectedIndustry && !selectedCompany && !selectedLabelKey}
            >
              Clear selection
            </button>
          </div>
        </div>

        <div className="lg:col-span-2">
          <div className="card">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              Template Details
            </h2>
            {(selectedTemplate || selectedFromList) ? (
              <div className="space-y-4">
                <div>
                  <label className="label">Template Key</label>
                  <p className="text-gray-900 font-mono text-sm bg-gray-50 p-2 rounded">
                    {(selectedTemplate || selectedFromList)?.templateKey}
                  </p>
                </div>
                <div>
                  <label className="label">Industry</label>
                  <p className="text-gray-900">{(selectedTemplate || selectedFromList)?.industry || 'unknown'}</p>
                </div>
                <div>
                  <label className="label">Company</label>
                  <p className="text-gray-900">{(selectedTemplate || selectedFromList)?.company || (selectedTemplate || selectedFromList)?.retailer}</p>
                </div>
                <div>
                  <label className="label">Label Type</label>
                  <p className="text-gray-900">{(selectedTemplate || selectedFromList)?.labelType}</p>
                </div>
                <div>
                  <label className="label">Version</label>
                  <p className="text-gray-900">{(selectedTemplate || selectedFromList)?.version}</p>
                </div>
                <div>
                  <label className="label">Fields</label>
                  <div className="space-y-2">
                    {(() => {
                      const tmpl = selectedTemplate || selectedFromList;
                      const rawFields = tmpl?.fields || [];
                      // Normalize: handle both string[] (legacy) and TemplateField[] formats
                      const normalized = rawFields.map((f: any) =>
                        typeof f === 'string' ? { name: f, type: 'string', required: true } : f
                      );
                      if (normalized.length === 0) {
                        return (
                          <p className="text-gray-400 text-sm italic">No fields defined</p>
                        );
                      }
                      return normalized.map((field: { name: string; type: string; required: boolean }) => (
                        <div
                          key={field.name}
                          className="flex justify-between items-center text-sm bg-gray-50 p-2 rounded"
                        >
                          <span className="font-mono">{field.name}</span>
                          <div className="flex items-center space-x-2">
                            <span className="text-gray-500">{field.type}</span>
                            {field.required ? (
                              <span className="text-red-500 text-xs font-medium">required</span>
                            ) : (
                              <span className="text-gray-400 text-xs">optional</span>
                            )}
                          </div>
                        </div>
                      ));
                    })()}
                  </div>
                </div>
              </div>
            ) : (
              <p className="text-gray-500 text-center py-8">
                Select industry, company, and label to view details
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
